
import React from 'react';
import { Loader } from 'lucide-react';

interface ArticleReferencesProps {
  summaries: any[];
  isLoading: boolean;
}

export const ArticleReferences: React.FC<ArticleReferencesProps> = ({ summaries, isLoading }) => {
  // Debug logging
  console.log('[ArticleReferences] Received summaries:', summaries);
  console.log('[ArticleReferences] isLoading:', isLoading);
  console.log('[ArticleReferences] summaries.length:', summaries.length);
  
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-400">
        <Loader className="h-4 w-4 animate-spin" />
        <span>Loading article references...</span>
      </div>
    );
  }

  if (summaries.length === 0) {
    return <p className="text-sm text-gray-500">No article references for this category.</p>;
  }

  return (
    <div className="space-y-2">
      <h5 className="font-semibold text-gray-300">Article References</h5>
      <ul className="space-y-2">
        {summaries.map((summary) => {
          // Handle both old and new data structures
          const summaryText = summary.summary_text || summary.text || '';
          const relevanceScore = summary.relevance_score;
          const categories = summary.categories;
          
          // For the main article link, use article_url if available, otherwise use first source
          const mainUrl = summary.article_url || (summary.sources && summary.sources[0]?.url) || '';
          const mainTitle = summary.title || (summary.sources && summary.sources[0]?.title) || summaryText.substring(0, 50);
          
          console.log(`[ArticleReferences] Processing summary ${summary.id}:`, {
            summaryText: summaryText.substring(0, 50),
            mainUrl,
            mainTitle,
            sourcesCount: summary.sources?.length || 0
          });
          
          return (
            <li key={summary.id} className="p-2 bg-gray-800 rounded-md space-y-2">
              {/* Main summary with primary link */}
              <div>
                {mainUrl ? (
                  <a href={mainUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                    {mainTitle || `${summaryText.substring(0, 100)}...`}
                  </a>
                ) : (
                  <span className="text-gray-300">{summaryText.substring(0, 100)}...</span>
                )}
                
                <div className="text-xs text-gray-500 mt-1">
                  {relevanceScore && <span>Relevance: {relevanceScore}</span>}
                  {categories && (
                    <>
                      {relevanceScore && ' | '}
                      <span>Categories: {Array.isArray(categories) ? categories.join(', ') : JSON.stringify(categories)}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Additional sources if available */}
              {summary.sources && summary.sources.length > 1 && (
                <div className="ml-4 space-y-1">
                  <span className="text-xs text-gray-400">Additional Sources:</span>
                  {summary.sources.slice(1).map((source, index) => (
                    <div key={index} className="text-xs">
                      <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:underline">
                        {source.title || source.url}
                      </a>
                      {source.relevance_score && (
                        <span className="text-gray-500 ml-2">({source.relevance_score})</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Summary text */}
              {summaryText && (
                <div className="text-xs text-gray-400 mt-2">
                  <details>
                    <summary className="cursor-pointer hover:text-gray-300">View Summary</summary>
                    <p className="mt-1 text-gray-400 whitespace-pre-wrap">{summaryText}</p>
                  </details>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
